import {ProductNs, SettingNs, type SidebarNs} from '@shared/types'
import {
  AzureBoardsView,
  CompanyView,
  confirmAzureBoardsAuthenticationResolver,
  confirmGoogleDriveAuthenticationResolver,
  confirmJiraAuthenticationResolver,
  FigmaView,
  getCompanyInfoResolver,
  getMemberDetailsResolver,
  getProductsResolver,
  getWorkspaceIntegrationResolver,
  getWorkspaceMembersResolver,
  GoogleDriveView,
  IntegrationsDetailView,
  IntegrationsLayout,
  IntegrationsView,
  InviteTeamMembersView,
  JiraCloudView,
  MemberDetailsView,
  MembersLayout,
  MembersView,
  SlackView,
  SuggestEmailView,
  ThemesView,
  UpgradeView
} from '@domains/settings'
import {type NavigationGuardNext, type RouteLocationNormalized} from 'vue-router'
import {SettingsLayout} from "@shared/components";
import { confirmFigmaAuthenticationResolver } from '@domains/settings/resolvers/confirm-figma-authentication.resolver';
import {getCurrentProductResolver} from "@domains/settings/resolvers/get-current-product.resolver";
import BillingsView from "@domains/settings/views/billings/billings.view.vue";


export const settingRoutes = [
  {
    path: '/settings',
    name: SettingNs.SettingRouteEnums.SETTINGS,
    component: SettingsLayout,
    redirect: {name: SettingNs.SettingRouteEnums.THEMES},
    meta: {
      breadcrumb: 'Settings',
      data: {
        menu: [
          {
            title: 'Personal Settings',
            menuItems: [
              {
                name: SettingNs.SettingRouteEnums.THEMES,
                label: 'Themes',
                icon: 'Moon'
              }
            ]
          },
          {
            title: 'Workspace Settings',
            userRole: ['ADMIN', 'OWNER', 'MEMBER'],
            menuItems: [
              {
                name: SettingNs.SettingRouteEnums.COMPANY,
                label: 'Company',
                icon: 'Building',
                userRole: ['ADMIN', 'OWNER']
              },
              {
                name: SettingNs.SettingRouteEnums.MEMBERS,
                label: 'Members',
                icon: 'Users',
                userRole: ['ADMIN', 'OWNER']
              },
              {
                name: SettingNs.SettingRouteEnums.INTEGRATIONS,
                label: 'Integrations',
                icon: 'Settings',
                userRole: ['OWNER', 'ADMIN', 'MEMBER']
              },
              {
                name: SettingNs.SettingRouteEnums.BILLING,
                label: 'Billings',
                icon: 'Receipt',
                userRole: ['OWNER'],
                product: [ProductNs.ProductType.PROFESSIONAL, ProductNs.ProductType.BUSINESS]
              },
              {
                name: SettingNs.SettingRouteEnums.UPGRADE,
                label: 'Upgrade',
                icon: 'Upload',
                userRole: ['OWNER']
              }
            ]
          }
        ]
      } as SidebarNs.SidebarContent
    },
    children: [
      {
        path: 'themes',
        name: SettingNs.SettingRouteEnums.THEMES,
        component: ThemesView,
        meta: {
          breadcrumb: 'Themes'
        }
      },
      {
        path: 'company',
        name: SettingNs.SettingRouteEnums.COMPANY,
        component: CompanyView,
        meta: {
          userRole: ['ADMIN', 'OWNER'],
          breadcrumb: 'Company'
        },
        beforeEnter: [
          (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void => {
            getCompanyInfoResolver(to, from, next)
          }
        ]
      },
      {
        path: 'members',
        name: SettingNs.SettingRouteEnums.MEMBERS,
        component: MembersLayout,
        redirect: {name: SettingNs.SettingRouteEnums.MEMBERSPAGE},
        meta: {
          breadcrumb: 'Members'
        },
        beforeEnter: [
          (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void => {
            getWorkspaceMembersResolver().then(() => next());
          }
        ],
        children: [
          {
            path: '',
            name: SettingNs.SettingRouteEnums.MEMBERSPAGE,
            component: MembersView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              activeMenu: SettingNs.SettingRouteEnums.MEMBERS
            }
          },
          {
            path: ':memberId',
            name: SettingNs.SettingRouteEnums.MEMBERDETAILS,
            component: MemberDetailsView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              breadcrumb: 'Member Details',
              activeMenu: SettingNs.SettingRouteEnums.MEMBERS
            },
            props: true,
            beforeEnter: [
              (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void => {
                getMemberDetailsResolver(to, from, next)
              }
            ]
          },
          {
            path: 'suggest-change-email',
            name: SettingNs.SettingRouteEnums.SUGGESTEMAIL,
            component: SuggestEmailView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              breadcrumb: 'Suggest Change Email',
              activeMenu: SettingNs.SettingRouteEnums.MEMBERS
            }
          },
          {
            path: 'invite',
            name: SettingNs.SettingRouteEnums.INVITETEAMMEMBERS,
            component: InviteTeamMembersView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              breadcrumb: 'Invite Team Members',
              activeMenu: SettingNs.SettingRouteEnums.MEMBERS
            }
          }
        ]
      },
      {
        path: 'integrations',
        name: SettingNs.SettingRouteEnums.INTEGRATIONS,
        component: IntegrationsLayout,
        redirect: {name: SettingNs.SettingRouteEnums.INTEGRATIONSPAGE},
        meta: {
          breadcrumb: 'Integrations'
        },
        beforeEnter: [
          (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void => {
            getWorkspaceIntegrationResolver(to, from, next)
          }
        ],
        children: [
          {
            path: '',
            name: SettingNs.SettingRouteEnums.INTEGRATIONSPAGE,
            component: IntegrationsView,
            meta: {
              userRole: ['ADMIN', 'OWNER', 'MEMBER'],
              activeMenu: SettingNs.SettingRouteEnums.INTEGRATIONS
            }
          },
          {
            path: ':integration',
            name: SettingNs.SettingRouteEnums.INTEGRATIONS_DETAIL,
            component: IntegrationsDetailView,
            meta: {
              userRole: ['OWNER'],
              breadcrumb: 'Detail',
              activeMenu: SettingNs.SettingRouteEnums.INTEGRATIONS
            }
          },
          {
            path: 'jira/integration',
            component: JiraCloudView,
            meta: {
              userRole: ['OWNER'],
              breadcrumb: 'Jira',
              activeMenu: SettingNs.SettingRouteEnums.INTEGRATIONS
            }
          },
          {
            path: 'figma/integration',
            component: FigmaView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              breadcrumb: 'Figma',
              activeMenu: SettingNs.SettingRouteEnums.INTEGRATIONS
            }
          },
          {
            path: 'figma/confirm',
            component: IntegrationsView,
            beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
              confirmFigmaAuthenticationResolver(to, from, next)
            }
          },
          {
            path: 'google-drive/integration',
            component: GoogleDriveView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              breadcrumb: 'Google Drive',
              activeMenu: SettingNs.SettingRouteEnums.INTEGRATIONS
            }
          },
          {
            path: 'google-drive/confirm',
            component: IntegrationsView,
            beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
              confirmGoogleDriveAuthenticationResolver(to, from, next)
            }
          },
          {
            path: 'slack/integration',
            component: SlackView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              breadcrumb: 'Slack',
              activeMenu: SettingNs.SettingRouteEnums.INTEGRATIONS
            }
          },
          {
            path: 'jira/confirm',
            component: IntegrationsView,
            beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
              confirmJiraAuthenticationResolver(to, from, next)
            }
          },
          {
            path: 'azure-boards/integration',
            component: AzureBoardsView,
            meta: {
              userRole: ['ADMIN', 'OWNER'],
              breadcrumb: 'Azure Boards',
              activeMenu: SettingNs.SettingRouteEnums.INTEGRATIONS
            }
          },
          {
            path: 'azure-boards/confirm',
            component: IntegrationsView,
            beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
              confirmAzureBoardsAuthenticationResolver(to, from, next)
            }
          }
        ]
      },
      {
        path: 'billings',
        name: SettingNs.SettingRouteEnums.BILLING,
        component: BillingsView
      },
      {
        path: 'upgrade',
        name: SettingNs.SettingRouteEnums.UPGRADE,
        component: UpgradeView,
        meta: {
          breadcrumb: 'Upgrade'
        },
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          Promise.all([
            getCurrentProductResolver(),
            getProductsResolver(),
            getWorkspaceMembersResolver()
          ]).then(() => {
            next();
          });
        }
      }
    ]
  }
]
